import { Directive, ElementRef, HostListener, Optional, Self } from "@angular/core";
import { NgControl } from "@angular/forms";

/**
 * Restricts an input to the digits 0-9 only.
 *
 * Every way a character can reach the field is guarded:
 * - `beforeinput` stops typed / soft keyboard / IME text before it is inserted
 * - `keypress` is the fallback for browsers that do not fire `beforeinput`
 * - `input` cleans up whatever still slipped through (autofill, drag & drop, paste)
 *
 * Paste and drop are deliberately not blocked so a pasted "1 2 3 4 5 6" still fills the boxes,
 * the non-digits are stripped in `onInput` instead.
 */
@Directive({
    selector: "[otpDigitsOnly]",
})
export class OtpDigitsOnlyDirective {
    constructor(
        private el: ElementRef<HTMLInputElement>,
        @Optional() @Self() private control: NgControl
    ) {}

    @HostListener("beforeinput", ["$event"])
    onBeforeInput(event: InputEvent): void {
        const inputType = event.inputType || "";
        const isPasteOrDrop = inputType === "insertFromPaste" || inputType === "insertFromDrop";

        if (!inputType.startsWith("insert") || isPasteOrDrop) return;

        if (event.data && /[^0-9]/.test(event.data)) {
            event.preventDefault();
        }
    }

    @HostListener("keypress", ["$event"])
    onKeypress(event: KeyboardEvent): void {
        // let shortcuts (copy/paste/select all) and non-printable keys (Enter, Tab, Backspace) through
        if (event.ctrlKey || event.metaKey || event.key.length > 1) return;

        if (!/^[0-9]$/.test(event.key)) {
            event.preventDefault();
        }
    }

    @HostListener("input")
    onInput(): void {
        const input = this.el.nativeElement;
        const digits = input.value.replace(/[^0-9]/g, "");

        if (input.value !== digits) {
            input.value = digits;
        }

        const controlValue = this.control?.control?.value;
        if (controlValue != null && String(controlValue) !== digits) {
            this.control?.control?.setValue(digits, { emitEvent: false });
        }
    }
}
